<script setup lang="ts">
import {
  Circle as CircleIcon,
  Pause as PauseIcon,
  Play as PlayIcon,
  Save as SaveIcon,
  Trash2 as TrashIcon,
} from '@lucide/vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'

import BaseModal from '@/components/BaseModal.vue'
import BaseCheckbox from '@/components/ui/BaseCheckbox.vue'
import { useRecordingStore } from '@/stores/recordingStore'

const recordingStore = useRecordingStore()
const now = ref(Date.now())
let timer: ReturnType<typeof setInterval> | null = null

const formatDuration = (milliseconds: number): string => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (value: number) => String(value).padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

const liveDurations = computed(() => recordingStore.getLiveDurations(now.value))
const totalDuration = computed(() => formatDuration(liveDurations.value.elapsedMs))
const activeDuration = computed(() => formatDuration(liveDurations.value.activeMs))

const distanceText = computed(() => {
  const meters = recordingStore.liveDistanceMeters
  return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`
})

const ascentText = computed(() => `+${Math.round(recordingStore.liveElevationGain)} m`)

const hasSession = computed(() => recordingStore.hasRecording || recordingStore.hasStoppedDraft)

const statusText = computed(() => {
  if (recordingStore.isAutoPaused) return 'Auto-paused'
  if (recordingStore.isActive) return 'Recording'
  if (recordingStore.isPaused) return 'Paused'
  if (recordingStore.hasStoppedDraft) return 'Stopped'
  return 'Ready'
})

const autoPauseEnabled = computed({
  get: () => recordingStore.autoPauseEnabled,
  set: (enabled: boolean) => {
    void recordingStore.setAutoPauseEnabled(enabled)
  },
})

const canSave = computed(
  () =>
    recordingStore.hasRecording ||
    (recordingStore.hasStoppedDraft && (recordingStore.draft?.pointCount ?? 0) >= 2),
)

const handlePrimaryAction = async () => {
  if (recordingStore.hasRecording || recordingStore.hasStoppedDraft) {
    await recordingStore.stopAndSave()
    return
  }

  await recordingStore.start()
}

onMounted(() => {
  timer = setInterval(() => {
    now.value = Date.now()
  }, 1000)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<template>
  <BaseModal
    :is-open="recordingStore.isModalOpen"
    title="Record"
    size="md"
    :close-on-backdrop-click="!recordingStore.isBusy"
    :closeable="!recordingStore.isBusy"
    content-class="max-h-[calc(100dvh-2rem)]"
    @close="recordingStore.closeModal"
  >
    <div class="space-y-5">
      <div
        class="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-4 py-3"
      >
        <div class="flex items-center gap-3">
          <span
            class="flex h-3 w-3 rounded-full"
            :class="
              recordingStore.isActive
                ? 'bg-red-600 animate-pulse'
                : recordingStore.isAutoPaused
                  ? 'bg-amber-500'
                  : 'bg-gray-300'
            "
          />
          <div>
            <p class="text-sm font-semibold text-gray-900">{{ statusText }}</p>
          </div>
        </div>
        <div class="text-right" title="Total time, including breaks">
          <p class="font-mono text-lg font-semibold text-gray-900">{{ totalDuration }}</p>
          <p v-if="hasSession" class="text-xs text-gray-500">Total time</p>
        </div>
      </div>

      <div v-if="hasSession" class="grid grid-cols-3 gap-2">
        <div class="rounded-md border border-gray-200 px-3 py-2">
          <p class="text-xs text-gray-500">Distance</p>
          <p class="font-mono text-sm font-semibold text-gray-900">{{ distanceText }}</p>
        </div>
        <div class="rounded-md border border-gray-200 px-3 py-2">
          <p class="text-xs text-gray-500">Ascent</p>
          <p class="font-mono text-sm font-semibold text-gray-900">{{ ascentText }}</p>
        </div>
        <div
          class="rounded-md border border-gray-200 px-3 py-2"
          title="Recording time, excluding breaks"
        >
          <p class="text-xs text-gray-500">Active time</p>
          <p class="font-mono text-sm font-semibold text-gray-900">{{ activeDuration }}</p>
        </div>
      </div>

      <label
        class="flex items-start gap-3 rounded-md border border-gray-200 px-4 py-3 hover:cursor-pointer"
      >
        <BaseCheckbox
          v-model="autoPauseEnabled"
          color="danger"
          :disabled="recordingStore.isBusy || !recordingStore.isSupported"
          class="mt-0.5"
          aria-describedby="auto-pause-hint"
        />
        <div class="min-w-0">
          <span class="text-sm font-medium text-gray-700">Auto pause</span>
          <p id="auto-pause-hint" class="mt-0.5 text-xs text-gray-500">
            Pause while stationary and resume automatically when movement continues.
          </p>
        </div>
      </label>

      <p
        v-if="recordingStore.errorMessage"
        class="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
      >
        {{ recordingStore.errorMessage }}
      </p>

      <p
        v-if="!recordingStore.isSupported"
        class="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800"
      >
        Track recording is only available in the Tauri mobile app.
      </p>

      <div class="flex flex-wrap gap-2">
        <button
          class="inline-flex min-h-10 items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="
            recordingStore.isBusy ||
            !recordingStore.isSupported ||
            (recordingStore.hasStoppedDraft && !canSave)
          "
          @click="handlePrimaryAction"
        >
          <CircleIcon
            v-if="!recordingStore.hasRecording && !recordingStore.hasStoppedDraft"
            class="h-4 w-4 fill-current"
          />
          <SaveIcon v-else class="h-4 w-4" />
          <span>{{
            recordingStore.hasRecording || recordingStore.hasStoppedDraft
              ? 'Stop & Save'
              : 'Start Recording'
          }}</span>
        </button>

        <button
          v-if="recordingStore.isActive"
          class="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="recordingStore.isBusy"
          @click="recordingStore.pause"
        >
          <PauseIcon class="h-4 w-4" />
          Pause
        </button>

        <button
          v-if="recordingStore.isPaused"
          class="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="recordingStore.isBusy"
          @click="recordingStore.resume"
        >
          <PlayIcon class="h-4 w-4" />
          Resume
        </button>

        <button
          v-if="recordingStore.hasRecording || recordingStore.hasStoppedDraft"
          class="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="recordingStore.isBusy"
          @click="recordingStore.discard"
        >
          <TrashIcon class="h-4 w-4" />
          Discard
        </button>
      </div>
    </div>
  </BaseModal>
</template>
